/**
 * LongTextField - tappable preview that opens a modal multi-line editor.
 *
 * Inline single-line inputs are unreadable once the text grows (a thesis
 * catalyst or risk easily runs past one line), so this control renders a
 * wrapped two-line preview and opens a full-width text area in a modal on tap.
 * The parent owns the value; edits are committed only when the user saves.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  KeyboardAvoidingView,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';

export interface LongTextFieldProps {
  /** Current value, owned by the parent. */
  value: string;
  /** Called with the new value when the user saves the modal editor. */
  onChangeText: (text: string) => void;
  placeholder: string;
  /** Heading of the editor modal, e.g. "Edit Pillar". */
  modalTitle: string;
  maxLength?: number;
  editable?: boolean;
  /** testID of the tappable preview row; modal parts derive from it. */
  testID?: string;
}

export function LongTextField({
  value,
  onChangeText,
  placeholder,
  modalTitle,
  maxLength,
  editable = true,
  testID,
}: LongTextFieldProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState(value);

  const modalTestID = testID ? `${testID}-modal` : undefined;
  const editorTestID = testID ? `${testID}-editor` : undefined;
  const closeTestID = testID ? `${testID}-close` : undefined;
  const cancelTestID = testID ? `${testID}-cancel` : undefined;
  const saveTestID = testID ? `${testID}-save` : undefined;

  const handleOpen = useCallback(() => {
    if (!editable) return;
    setDraft(value);
    setIsOpen(true);
  }, [editable, value]);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSave = useCallback(() => {
    onChangeText(draft);
    setIsOpen(false);
  }, [draft, onChangeText]);

  return (
    <>
      <TouchableOpacity
        testID={testID}
        style={[styles.preview, !editable && styles.previewDisabled]}
        onPress={handleOpen}
        disabled={!editable}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel={modalTitle}
        accessibilityHint={t('portfolio.tapToEditHint', {
          defaultValue: 'Opens a full text area to view and edit',
        })}
      >
        <Text
          style={value ? styles.previewText : styles.previewPlaceholder}
          numberOfLines={2}
          ellipsizeMode="tail"
        >
          {value || placeholder}
        </Text>
        <Ionicons name="expand-outline" size={16} color={theme.colors.textTertiary} />
      </TouchableOpacity>

      <Modal
        visible={isOpen}
        transparent
        animationType="fade"
        onRequestClose={handleCancel}
        testID={modalTestID}
      >
        <KeyboardAvoidingView
          style={styles.overlay}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        >
          <View style={styles.dialog}>
            <View style={styles.dialogHeader}>
              <Text style={styles.dialogTitle}>{modalTitle}</Text>
              <TouchableOpacity
                testID={closeTestID}
                onPress={handleCancel}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                accessibilityRole="button"
                accessibilityLabel={t('common.close', { defaultValue: 'Close' })}
              >
                <Ionicons name="close" size={22} color={theme.colors.text} />
              </TouchableOpacity>
            </View>

            <ScrollView
              style={styles.editorScroll}
              contentContainerStyle={styles.editorScrollContent}
              keyboardShouldPersistTaps="handled"
            >
              <TextInput
                testID={editorTestID}
                style={styles.editor}
                value={draft}
                onChangeText={setDraft}
                placeholder={placeholder}
                placeholderTextColor={theme.colors.textTertiary}
                multiline
                numberOfLines={6}
                maxLength={maxLength}
                editable={editable}
                autoFocus
                textAlignVertical="top"
              />
            </ScrollView>

            {maxLength ? (
              <Text style={styles.counter}>
                {draft.length} / {maxLength}
              </Text>
            ) : null}

            <View style={styles.actions}>
              <TouchableOpacity
                testID={cancelTestID}
                style={[styles.actionButton, styles.cancelButton]}
                onPress={handleCancel}
              >
                <Text style={styles.cancelButtonText}>
                  {t('common.cancel', { defaultValue: 'Cancel' })}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                testID={saveTestID}
                style={[styles.actionButton, styles.saveButton]}
                onPress={handleSave}
              >
                <Text style={styles.saveButtonText}>
                  {t('common.save', { defaultValue: 'Save' })}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    preview: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderColor: theme.colors.border,
      backgroundColor: theme.colors.backgroundElevated,
      borderRadius: theme.radii.md,
      padding: theme.spacing.sm,
      minHeight: 44,
    },
    previewDisabled: {
      opacity: 0.6,
    },
    previewText: {
      flex: 1,
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.text,
      // lineHeightNormal is a multiplier, not a px value — multiply to get a real
      // line box, otherwise the text collapses and renders invisible.
      lineHeight: theme.typography.fontSizeMd * theme.typography.lineHeightNormal,
    },
    previewPlaceholder: {
      flex: 1,
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.textTertiary,
      fontStyle: 'italic',
      lineHeight: theme.typography.fontSizeMd * theme.typography.lineHeightNormal,
    },
    overlay: {
      flex: 1,
      backgroundColor: theme.colors.overlay,
      justifyContent: 'center',
      padding: theme.spacing.lg,
    },
    dialog: {
      width: '100%',
      maxWidth: 560,
      maxHeight: '85%',
      alignSelf: 'center',
      gap: theme.spacing.sm,
      backgroundColor: theme.colors.surface,
      borderWidth: theme.id === 'bauhaus' ? 2 : 0,
      borderColor: theme.colors.border,
      borderRadius: theme.radii.lg,
      padding: theme.spacing.lg,
    },
    dialogHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing.sm,
    },
    dialogTitle: {
      flex: 1,
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.colors.text,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    editorScroll: {
      flexGrow: 0,
      flexShrink: 1,
    },
    editorScrollContent: {
      flexGrow: 1,
    },
    editor: {
      minHeight: 140,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderColor: theme.colors.border,
      backgroundColor: theme.colors.backgroundElevated,
      borderRadius: theme.radii.md,
      padding: theme.spacing.sm,
      fontSize: theme.typography.fontSizeMd,
      lineHeight: theme.typography.fontSizeMd * theme.typography.lineHeightNormal,
      color: theme.colors.text,
    },
    counter: {
      fontSize: theme.typography.fontSizeXs,
      color: theme.colors.textTertiary,
      textAlign: 'right',
    },
    actions: {
      flexDirection: 'row',
      gap: theme.spacing.sm,
    },
    actionButton: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 48,
      paddingHorizontal: theme.spacing.md,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
    },
    cancelButton: {
      backgroundColor: theme.colors.backgroundElevated,
      borderColor: theme.colors.border,
    },
    cancelButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      color: theme.colors.textSecondary,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    saveButton: {
      backgroundColor: theme.colors.primary,
      borderColor: theme.colors.primary,
    },
    saveButtonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      color: theme.colors.textInverse,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
  });
}
